import { ContentType } from "@brookbush/database";
import type { Content } from "@brookbush/types";

import { BookOpenIcon } from "@heroicons/react/24/outline";
import { clsx } from "clsx";
import format from "date-fns/format";
import {
	AwardIcon,
	CircleMinusIcon,
	ClapperboardIcon,
	FileTextIcon,
	HelpCircle,
	PickaxeIcon,
	Share2Icon,
	VideoIcon,
} from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import posthog from "posthog-js";
import { useState, useTransition } from "react";

import ConfirmationModal from "./ConfirmationModal";
import { useBookmarks, usePassedCourses, useQueryParam } from "~/hooks";
import { getRoutePrefix } from "~/utils/helpers";

interface ContentCardProps extends Content {
	isRecommended?: boolean;
	isElevated?: boolean;
	bookmarkId?: number;
	analyticsSource?: string;
}

export const contentTypeMap: Record<
	ContentType,
	{
		className: string;
		icon: React.ComponentType<{ className: string }>;
		iconClassName?: string;
	}
> = {
	[ContentType.COURSE]: {
		className: "text-sky-700 dark:text-sky-200",
		icon: BookOpenIcon,
		iconClassName:
			"text-sky-700 dark:text-sky-200 bg-sky-100 dark:bg-sky-300/20 border-sky-300 dark:border-sky-300/30",
	},
	[ContentType.ARTICLE]: {
		className:
			"text-violet-700 dark:text-violet-200 border-violet-600/40 dark:border-violet-300/50",

		icon: FileTextIcon,
		iconClassName:
			"text-violet-700 dark:text-violet-200 bg-violet-100 dark:bg-violet-300/20 border-violet-300 dark:border-violet-300/30",
	},
	[ContentType.VIDEO]: {
		className:
			"text-pink-700 dark:text-pink-200 border-pink-600/40 dark:border-pink-300/50",
		icon: VideoIcon,
		iconClassName:
			"text-pink-700 dark:text-pink-200 bg-pink-100 dark:bg-pink-300/20 border-pink-300 dark:border-pink-300/30",
	},
	[ContentType.GLOSSARY]: {
		className:
			"text-orange-700 dark:text-orange-200 border-orange-600/40 dark:border-orange-300/30",
		icon: HelpCircle,
		iconClassName:
			"text-orange-700 dark:text-orange-200 bg-orange-100 dark:bg-orange-300/20 border-orange-300 dark:border-orange-300/30",
	},
	[ContentType.WEBINAR]: {
		className:
			"text-cyan-700 dark:text-cyan-200 border-cyan-600/40 dark:border-cyan-300/30",
		icon: ClapperboardIcon,
		iconClassName:
			"text-cyan-700 dark:text-cyan-200 bg-cyan-100 dark:bg-cyan-300/20 border-cyan-300 dark:border-cyan-300/30",
	},
	[ContentType.WORKSHOP]: {
		className:
			"dark:text-cyan-300 text-cyan-700 border-cyan-600/40 dark:border-cyan-300/30",
		icon: PickaxeIcon,
		iconClassName:
			"text-cyan-700 dark:text-cyan-300 bg-cyan-100 dark:bg-cyan-300/20 border-cyan-300 dark:border-cyan-300/30",
	},
	[ContentType.CERTIFICATION]: {
		className:
			"text-emerald-700 dark:text-emerald-200 border-emerald-600/40 dark:border-emerald-300/30",
		icon: AwardIcon,
		iconClassName:
			"text-emerald-700 dark:text-emerald-200 bg-emerald-100 dark:bg-emerald-300/20 border-emerald-300 dark:border-emerald-300/30",
	},
};

const ContentCard = (props: ContentCardProps) => {
	const [, setShareParam] = useQueryParam("share");
	const [isPending, startTransition] = useTransition();

	const { removeBookmark } = useBookmarks();
	const credits = props.credits
		? `${props.credits} credit${props.credits > 1 ? "s" : ""}`
		: null;

	const [isRemovingBookmark, setIsRemovingBookmark] = useState(false);

	const duration =
		props._type === ContentType.VIDEO && props.duration
			? format(new Date(0).setSeconds(props.duration), "mm:ss")
			: null;

	const contentType =
		props._type === "GLOSSARY" ? "Glossary Term" : props._type.toLowerCase();

	const { passedCourses } = usePassedCourses();

	const isPassed =
		props._type === ContentType.COURSE &&
		passedCourses?.some((course) => course.courseId === props._id);

	const Icon = contentTypeMap[props._type].icon;

	const href = `/${getRoutePrefix(props._type)}/${props.slug}`;

	return (
		<>
			<ConfirmationModal
				title="Remove Bookmark"
				description="Are you sure you want to remove this item from your list?"
				open={isRemovingBookmark}
				onClose={() => setIsRemovingBookmark(false)}
				onConfirm={async () => {
					startTransition(async () => {
						if (props.bookmarkId) {
							await removeBookmark(props.bookmarkId);
						}
						setIsRemovingBookmark(false);
					});
				}}
			/>
			<Link
				className={clsx(
					"relative flex h-24 min-w-0 shrink-0 grow-0 select-none gap-2.5 rounded-xl border bg-white p-2.5 shadow shadow-slate-200 hover:bg-slate-50 dark:bg-slate-800 dark:shadow-slate-950 dark:hover:bg-slate-700",
					{
						"border-emerald-600/40! bg-emerald-50/50! shadow-emerald-800/10! hover:bg-emerald-50! dark:border-emerald-300/20! dark:bg-emerald-800/30! dark:shadow-emerald-950/50! dark:hover:bg-emerald-800/50!":
							isPassed,
						"border-orange-400! shadow-[0_0_16px_2px_rgba(251,146,60,0.35)]! ring-2! ring-orange-400/50!":
							props.isRecommended && !isPassed,
						"shadow-xl": props.isElevated,
					},
				)}
				href={href}
				onClick={() => {
					if (props.analyticsSource) {
						posthog.capture("content_card:clicked", {
							source: props.analyticsSource,
							content_type: props._type,
							slug: props.slug,
						});
					}
				}}
			>
				{props.thumbnail ? (
					<div className="relative aspect-square h-full shrink-0 overflow-hidden rounded-xl border">
						<Image
							src={props.thumbnail?.url}
							alt={props.thumbnail.alt ?? props.title}
							fill
							className="object-cover"
						/>
					</div>
				) : (
					<div
						className={clsx(
							"flex aspect-square h-full items-center justify-center rounded-xl border",
							contentTypeMap[props._type].iconClassName,
						)}
					>
						<Icon className={clsx("size-6")} />
					</div>
				)}

				<div className="flex flex-1 flex-col gap-1">
					<div className="flex w-full justify-between gap-1 text-inherit">
						<div className="flex gap-2.5 font-medium text-sm capitalize">
							<span className={contentTypeMap[props._type].className}>
								{props.analyticsSource === "next_recommended" && "Current"}{" "}
								{contentType}
							</span>

							{credits && <span>{credits}</span>}
							{duration && <span>{duration}</span>}
							{isPassed && (
								<span className="text-emerald-700 dark:text-emerald-300">
									Passed
								</span>
							)}

							{props.isRecommended && !isPassed && (
								<span className="text-orange-700 dark:text-orange-300">
									Recommended
								</span>
							)}
						</div>

						<div className="mr-1 flex gap-2">
							{props.bookmarkId && (
								<button
									disabled={isPending}
									onClick={async (e) => {
										e.preventDefault();
										e.stopPropagation();

										setIsRemovingBookmark(true);
									}}
								>
									<CircleMinusIcon className="size-4 rounded-full text-red-800 hover:opacity-50 dark:text-red-300" />
								</button>
							)}

							<button
								onClick={(e) => {
									e.preventDefault();
									e.stopPropagation();
									setShareParam(href);

									posthog.capture("share_button_clicked", {
										content_path: href,
									});
								}}
								className={clsx(
									"rounded-full hover:opacity-50",
									contentTypeMap[props._type].className,
								)}
							>
								<Share2Icon className="size-4" />
							</button>
						</div>
					</div>

					<h2 className="mr-0.5 line-clamp-2 font-medium">{props.title}</h2>
				</div>
			</Link>
		</>
	);
};

export default ContentCard;
